import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button.jsx';
import { Alert } from '../ui/Alert.jsx';
import { useChat } from '../../hooks/useChat';
import { useSession } from '../../context/SessionContext';
import { useNavIntent } from '../../context/NavigationContext';
import SourcePanel from '../etch/SourcePanel.jsx';
import ChatThread from './ChatThread.jsx';
import Composer from './Composer.jsx';
import { resolveSources } from './trust';
import { classifyError, ERROR_COPY } from './classifyError.js';

const COMPACT_PANEL_BREAKPOINT = '(max-width: 1200px)';

// Kinds whose cause Retry cannot fix by itself — a spent monthly budget, a
// provider the tenant policy no longer allows, or a policy that cannot be
// read right now. Each renders its own hint instead (see the Alert below).
const NO_RETRY_KINDS = new Set(['quotaExceeded', 'providerNotAllowed', 'policyUnavailable']);

export { classifyError };

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    mq.addEventListener('change', onChange);
    setMatches(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/**
 * Chat and History — the Etch start surface (design spec §2.2, §9.1, §10.2).
 */
export function ChatView() {
  const {
    messages, isStreaming, streamingText, streamingSources, sourcesPending,
    error, connected, conversationId, ask, retry, cancel, rateMessage, clearMessages, canSend,
  } = useChat();
  const { clearHistory, sessionMode } = useSession();
  const { consumeIntent } = useNavIntent();
  const { t } = useTranslation();
  const isAxiom = sessionMode === 'axiom';

  const panelIsDrawer = useMediaQuery(COMPACT_PANEL_BREAKPOINT);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selection, setSelection] = useState({ id: null, sources: null });

  // Auto-ask a question handed over from Onboarding.
  useEffect(() => {
    const intent = consumeIntent();
    if (intent?.path === '/chat' && intent.data?.question && canSend) {
      ask(intent.data.question);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While an answer streams the panel follows the live sources; afterwards it
  // shows whatever the user last opened, falling back to the newest answer.
  const lastAssistant = useMemo(
    () => [...messages].reverse().find((message) => message.role === 'assistant') || null,
    [messages]
  );

  const panelSources = useMemo(() => {
    if (isStreaming) return resolveSources({ content: streamingText, sources: streamingSources });
    if (selection.sources) return selection.sources;
    if (lastAssistant) {
      return resolveSources({ content: lastAssistant.content, sources: lastAssistant.sources || [] });
    }
    return [];
  }, [isStreaming, streamingText, streamingSources, selection.sources, lastAssistant]);

  useEffect(() => {
    if (isStreaming && streamingSources.length > 0 && !panelIsDrawer) setPanelOpen(true);
  }, [isStreaming, streamingSources.length, panelIsDrawer]);

  const openSource = useCallback((sourceId, resolved) => {
    setSelection({ id: sourceId, sources: resolved || null });
    setPanelOpen(true);
  }, []);

  const handleClear = async () => {
    await clearHistory();
    clearMessages();
    setSelection({ id: null, sources: null });
  };

  const errorKind = classifyError(error);
  const offline = !connected;

  // The streaming announcement needs an end, not just a start: emptying the
  // live region announces nothing, and neither `aria-busy` on the body nor a
  // focus move signals "done" (WCAG 4.1.3). One message on the
  // streaming → idle transition, cleared afterwards so it is not re-announced.
  const wasStreamingRef = useRef(isStreaming);
  const [answerReady, setAnswerReady] = useState('');
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      const count = panelSources.length;
      setAnswerReady(
        count > 0 ? t('a11y.liveAnswerReadyWithSources', { count }) : t('a11y.liveAnswerReady')
      );
    } else if (isStreaming) {
      setAnswerReady('');
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, panelSources.length, t]);

  const liveMessage = isStreaming
    ? t('a11y.liveStreaming')
    : errorKind
      ? t(ERROR_COPY[errorKind])
      : answerReady;

  return (
    <div className="etch-chat" data-panel-open={panelOpen || undefined}>
      {/* No history column here: the app sidebar owns the grouped history and
          its search at every width (spec §8.2/§8.4). A second copy inside the
          chat rendered the whole list above the conversation. */}
      <section className="etch-chat__main" aria-labelledby="etch-chat-title">
        <div className="etch-chat__header">
          <header className="etch-page-header etch-chat__column">
            <div className="etch-page-header__title">
              <h1 id="etch-chat-title">{isAxiom ? t('chat.titleAxiom') : t('chat.title')}</h1>
            </div>
            <div className="etch-page-header__actions">
              <Button
                variant="ghost"
                size="compact"
                aria-expanded={panelOpen}
                onClick={() => setPanelOpen((open) => !open)}
              >
                <BookOpen size={14} aria-hidden="true" />
                <span>{panelOpen ? t('chat.sourcesHide') : t('chat.sources')}</span>
              </Button>
              {messages.length > 0 && (
                <Button variant="ghost" size="compact" onClick={handleClear}>
                  <Trash2 size={13} aria-hidden="true" />
                  <span>{t('chat.clearHistory')}</span>
                </Button>
              )}
            </div>
          </header>
        </div>

        <p className="etch-visually-hidden" role="status" aria-live="polite">{liveMessage}</p>

        {errorKind && errorKind !== 'error' && (
          <div className="etch-chat__column etch-chat__notice">
            <Alert
              tone={errorKind === 'offline' || errorKind === 'quotaExceeded' ? 'warning' : 'danger'}
              title={t(ERROR_COPY[errorKind])}
            >
              {errorKind === 'quotaExceeded' && t('states.quotaExceededHint')}
              {errorKind === 'providerNotAllowed' && t('states.providerNotAllowedHint')}
              {errorKind === 'policyUnavailable' && t('states.policyUnavailableHint')}
              {!NO_RETRY_KINDS.has(errorKind) && (
                <Button variant="secondary" size="compact" onClick={retry}>{t('states.retry')}</Button>
              )}
            </Alert>
          </div>
        )}

        <ChatThread
          messages={messages}
          isStreaming={isStreaming}
          streamingText={streamingText}
          streamingSources={streamingSources}
          error={errorKind === 'error' ? error : null}
          onSuggestionClick={ask}
          activeSourceId={selection.id}
          onOpenSource={openSource}
          onRate={rateMessage}
          onRetry={retry}
        />

        <div className="etch-chat__column etch-chat__composer">
          <Composer
            onSubmit={ask}
            onCancel={cancel}
            disabled={!canSend}
            isStreaming={isStreaming}
            conversationId={conversationId}
            offline={offline}
          />
        </div>
      </section>

      <SourcePanel
        variant={panelIsDrawer ? 'drawer' : 'panel'}
        open={panelOpen}
        sources={panelSources}
        activeId={selection.id}
        loading={sourcesPending}
        onSelect={(id) => setSelection((prev) => ({ ...prev, id }))}
        onClose={() => setPanelOpen(false)}
      />
    </div>
  );
}

export default ChatView;
